import {
  ChartDataRequest,
  DwChartTypes,
  DwFieldAggregate,
  DwFilterType,
  DwQueryFilterGroup,
  DwQueryHelper
} from '@devwareapps/devware-cap';
import {
  AppMetaData,
  AppMetaDataLookups,
  CheckRideOutcomeAllItems,
  CheckRideRequestSubStatusAllItems
} from '../../../../meta-data/app-meta-data.service';
import { FilterType } from '../../../shared/models/filter-type.enum';
import {
  ReportAggregateMeasure,
  ReportDashboardDefinition,
  ReportSummaryDefinition
} from '../models/report-dashboard-definition.model';

const attrs = AppMetaData.RoCheckrideFactBase.Attributes;

const totalCheckrides: ReportAggregateMeasure = {
  alias: 'TotalCheckrides',
  aggregateFunction: DwFieldAggregate.Count,
  field: attrs.CheckRideRequestId.fieldNameWithItemPrefix
};

const eligibleAssessments: ReportAggregateMeasure = {
  alias: 'EligibleAssessments',
  aggregateFunction: DwFieldAggregate.Sum,
  field: attrs.EligibleAssessmentCount.fieldNameWithItemPrefix
};

const passedAssessments: ReportAggregateMeasure = {
  alias: 'PassedAssessments',
  aggregateFunction: DwFieldAggregate.Sum,
  field: attrs.PassCount.fieldNameWithItemPrefix,
  legendDisplay: 'Pass',
  color: '#2b6edc'
};

const failedAssessments: ReportAggregateMeasure = {
  alias: 'FailedAssessments',
  aggregateFunction: DwFieldAggregate.Sum,
  field: attrs.FailCount.fieldNameWithItemPrefix,
  legendDisplay: 'Fail',
  color: '#d90429'
};

const performanceFilter = equalFilter(attrs.IsPerformanceEligible.fieldNameWithItemPrefix, true);
const completedFilter = equalFilter(
  attrs.CheckrideRequestSubStatusId.fieldNameWithItemPrefix,
  CheckRideRequestSubStatusAllItems.Completed
);
const initialFilter = equalFilter(attrs.IsInitial.fieldNameWithItemPrefix, true);
const initialPerformanceFilter = mergeFilters(performanceFilter, initialFilter);
const passedPerformanceFilter = mergeFilters(
  performanceFilter,
  greaterThanFilter(attrs.PassCount.fieldNameWithItemPrefix, 0)
);
const passedInitialPerformanceFilter = mergeFilters(
  initialPerformanceFilter,
  greaterThanFilter(attrs.PassCount.fieldNameWithItemPrefix, 0)
);
const disruptedFilter = orFilter([
  { field: attrs.CheckrideRequestSubStatusId.fieldNameWithItemPrefix, value: CheckRideRequestSubStatusAllItems.Cancelled },
  { field: attrs.CheckRideOutcomeId.fieldNameWithItemPrefix, value: CheckRideOutcomeAllItems.Discontinued }
]);

const summaries: ReportSummaryDefinition[] = [
  {
    key: 'all',
    measures: [totalCheckrides, eligibleAssessments, passedAssessments, failedAssessments],
    staticFilter: completedFilter
  },
  {
    key: 'initial',
    measures: [eligibleAssessments, passedAssessments],
    staticFilter: initialPerformanceFilter
  },
  {
    key: 'disrupted',
    measures: [totalCheckrides],
    staticFilter: disruptedFilter
  }
];

export const SchoolPerformanceDashboardDefinition: ReportDashboardDefinition = {
  key: 'school-performance',
  title: 'School Performance',
  description: 'Checkride outcomes, pass rates, locations, and training signals across all exams',
  iconClass: 'fa fa-chart-line',
  filters: [
    {
      config: {
        attr: attrs.CheckrideDateTime.fieldNameWithItemPrefix,
        filterType: DwFilterType.GreaterThanEqual,
        filterDisplay: 'Time Period',
        label: 'Time Period',
        showFilter: true,
        defaultValue: { mode: 'currentYear' },
        additionalConfig: {
          presentationType: FilterType.ReportDatePeriod,
          filterTypeKey: 'report-date-period',
          iconClass: 'fa fa-calendar-alt'
        }
      }
    },
    {
      config: {
        attr: attrs.CheckrideTypeId.fieldNameWithItemPrefix,
        filterType: DwFilterType.In,
        filterDisplay: 'Checkride Type',
        label: 'Checkride Type',
        showFilter: true,
        showAsLookup: true,
        additionalConfig: {
          presentationType: FilterType.MultiSelect,
          lookupId: AppMetaDataLookups.CheckRideTypeWithGrade,
          allowMultiple: true,
          iconClass: 'fa fa-certificate'
        }
      }
    },
    {
      config: {
        attr: attrs.CheckRideTestAttemptClassificationId.fieldNameWithItemPrefix,
        filterType: DwFilterType.In,
        filterDisplay: 'Attempt Classification',
        label: 'Attempt Classification',
        showFilter: true,
        showAsLookup: true,
        additionalConfig: {
          presentationType: FilterType.MultiSelect,
          lookupId: AppMetaDataLookups.CheckRideTestAttemptClassificationAllItems,
          allowMultiple: true,
          iconClass: 'fa fa-layer-group'
        }
      }
    },
    {
      config: {
        attr: attrs.CheckRideTestTypeId.fieldNameWithItemPrefix,
        filterType: DwFilterType.In,
        filterDisplay: 'Test Type',
        label: 'Test Type',
        showFilter: true,
        showAsLookup: true,
        additionalConfig: {
          presentationType: FilterType.MultiSelect,
          lookupId: AppMetaDataLookups.CheckRideTestTypeAllItems,
          allowMultiple: true,
          iconClass: 'fa fa-tasks'
        }
      }
    },
    {
      config: {
        attr: attrs.FlightSchoolExaminerId.fieldNameWithItemPrefix,
        filterType: DwFilterType.In,
        filterDisplay: 'Examiner / Check Instructor',
        label: 'Examiner / Check Instructor',
        showFilter: true,
        showAsLookup: true,
        additionalConfig: {
          presentationType: FilterType.MultiSelect,
          lookupId: AppMetaDataLookups.FlightSchoolExaminerBySchool,
          allowMultiple: true,
          iconClass: 'fa fa-user-check'
        }
      }
    },
    {
      config: {
        attr: attrs.FlightSchoolApplicantId.fieldNameWithItemPrefix,
        filterType: DwFilterType.In,
        filterDisplay: 'Student',
        label: 'Student',
        showFilter: true,
        showAsLookup: true,
        additionalConfig: {
          presentationType: FilterType.MultiSelect,
          lookupId: AppMetaDataLookups.FlightSchoolApplicantByFlightSchool,
          allowMultiple: true,
          iconClass: 'fa fa-user-graduate'
        }
      }
    },
    {
      config: {
        attr: attrs.CheckRideOutcomeId.fieldNameWithItemPrefix,
        filterType: DwFilterType.In,
        filterDisplay: 'Outcome',
        label: 'Outcome',
        showFilter: true,
        showAsLookup: true,
        additionalConfig: {
          presentationType: FilterType.MultiSelect,
          lookupId: AppMetaDataLookups.CheckRideOutcomeAllItems,
          allowMultiple: true,
          iconClass: 'fa fa-flag-checkered'
        }
      }
    },
    {
      config: {
        attr: attrs.CheckrideAirportId.fieldNameWithItemPrefix,
        filterType: DwFilterType.In,
        filterDisplay: 'Airport',
        label: 'Airport',
        showFilter: true,
        showAsLookup: true,
        additionalConfig: {
          presentationType: FilterType.MultiSelect,
          lookupId: AppMetaDataLookups.AirportByFlightSchool,
          allowMultiple: true,
          iconClass: 'fa fa-map-marker-alt'
        }
      }
    },
    {
      config: {
        attr: attrs.FlightSchoolInstructorId.fieldNameWithItemPrefix,
        filterType: DwFilterType.In,
        filterDisplay: 'Instructor',
        label: 'Instructor',
        showFilter: true,
        showAsLookup: true,
        additionalConfig: {
          presentationType: FilterType.MultiSelect,
          lookupId: AppMetaDataLookups.FlightSchoolInstructorBySchool,
          allowMultiple: true,
          iconClass: 'fa fa-chalkboard-teacher'
        }
      }
    }
  ],
  summaries,
  tiles: [
    {
      tileKey: 'total-checkrides',
      tileDisplay: 'Total Checkrides',
      tileIconClass: 'fa fa-clipboard-check',
      additionalConfig: {
        summaryKey: 'all',
        value: { field: 'TotalCheckrides' },
        format: 'number',
          supportingText: row => `${formatNumber(row.EligibleAssessments)} eligible outcomes`,
        drillDownKey: 'checkrides'
      }
    },
    {
      tileKey: 'overall-pass-rate',
      tileDisplay: 'Overall Pass Rate',
      tileIconClass: 'fa fa-percent',
      additionalConfig: {
        summaryKey: 'all',
        value: { numeratorField: 'PassedAssessments', denominatorField: 'EligibleAssessments' },
        format: 'percent',
        supportingText: row => `${formatNumber(row.PassedAssessments)}/${formatNumber(row.EligibleAssessments)} passed`,
        drillDownKey: 'checkrides',
        detailFilter: passedPerformanceFilter
      }
    },
    {
      tileKey: 'first-time-pass-rate',
      tileDisplay: 'First-Time Pass Rate',
      tileIconClass: 'fa fa-award',
      additionalConfig: {
        summaryKey: 'initial',
        value: { numeratorField: 'PassedAssessments', denominatorField: 'EligibleAssessments' },
        format: 'percent',
        supportingText: row => `${formatNumber(row.PassedAssessments)}/${formatNumber(row.EligibleAssessments)} first attempts`,
        drillDownKey: 'checkrides',
        detailFilter: passedInitialPerformanceFilter
      }
    },
    {
      tileKey: 'disrupted',
      tileDisplay: 'Cancelled / Discontinued',
      tileIconClass: 'fa fa-exclamation-circle',
      additionalConfig: {
          summaryKey: 'disrupted',
          value: { field: 'TotalCheckrides' },
          format: 'number',
          drillDownKey: 'checkrides'
      }
    }
  ],
  widgets: [
    {
      key: 'pass-rates-by-attempt',
      title: 'Pass Rates by Attempt',
      description: 'First-time vs retest pass rates for completed checkrides',
      type: 'chart',
      request: chartRequest(
        DwChartTypes.bar,
        attrs.AttemptClassification.fieldNameWithItemPrefix,
        attrs.AttemptClassification.fieldName,
        true
      ),
      measures: [eligibleAssessments, passedAssessments],
      computedMeasures: [{
        alias: 'PassRate',
        numeratorField: 'PassedAssessments',
        denominatorField: 'EligibleAssessments',
        multiplier: 100,
        decimals: 0,
        legendDisplay: 'Pass Rate',
        color: '#2b6edc',
        valueSuffix: '%'
      }],
      staticFilter: performanceFilter,
      categoryOrder: ['Initial', 'Retest'],
      showGridTotals: false,
      showValues: true,
      valueAxisMax: 100,
      drillDownKey: 'checkrides'
    },
    {
      key: 'outcomes-by-checkride-type',
      title: 'Outcomes by Checkride Type',
      description: 'Pass and fail results per certificate or rating',
      type: 'chart',
      request: chartRequest(
        DwChartTypes.column,
        attrs.CheckrideTypeShortName.fieldNameWithItemPrefix,
        attrs.CheckrideTypeShortName.fieldName
      ),
      measures: [{ ...eligibleAssessments, showInChart: false }, passedAssessments, failedAssessments],
      staticFilter: performanceFilter,
      sortField: 'EligibleAssessments',
      maxItems: 10,
      showValues: true,
      drillDownKey: 'checkrides'
    },
    {
      key: 'outcomes-by-test-type',
      title: 'Outcomes by Test Type',
      description: 'Pass and fail results by practical, stage, or retest type',
      type: 'chart',
      request: chartRequest(
        DwChartTypes.column,
        attrs.CheckRideTestTypeId.fieldNameWithItemPrefix,
        'CheckRideTestTypeIdDisplay'
      ),
      measures: [{ ...eligibleAssessments, showInChart: false }, passedAssessments, failedAssessments],
      staticFilter: performanceFilter,
      sortField: 'EligibleAssessments',
      maxItems: 10,
      showValues: true,
      drillDownKey: 'checkrides'
    },
    {
      key: 'exams-by-airport',
      title: 'Exams by Airport',
      description: 'Checkride volume per airport',
      type: 'chart',
      request: chartRequest(
        DwChartTypes.column,
        attrs.CheckrideAirportId.fieldNameWithItemPrefix,
        'CheckrideAirportIdDisplay',
        true
      ),
      measures: [totalCheckrides],
      staticFilter: completedFilter,
      sortField: 'TotalCheckrides',
      maxItems: 8,
      showValues: true,
      drillDownKey: 'checkrides'
    },
    {
      key: 'exams-by-examiner',
      title: 'Exams by Examiner',
      description: 'Completed checkride volume per examiner or check instructor',
      type: 'chart',
      request: chartRequest(
        DwChartTypes.column,
        attrs.FlightSchoolExaminerId.fieldNameWithItemPrefix,
        'FlightSchoolExaminerIdDisplay',
        true
      ),
      measures: [totalCheckrides],
      staticFilter: completedFilter,
      sortField: 'TotalCheckrides',
      maxItems: 10,
      showValues: true,
      drillDownKey: 'checkrides'
    },
    {
      key: 'first-time-pass-rate-by-instructor',
      title: 'First-Time Pass Rate by Instructor',
      description: 'School checkrides with an assigned flight-school instructor',
      type: 'chart',
      request: chartRequest(
        DwChartTypes.column,
        attrs.FlightSchoolInstructorId.fieldNameWithItemPrefix,
        'FlightSchoolInstructorIdDisplay',
        true
      ),
      measures: [eligibleAssessments, passedAssessments],
      computedMeasures: [{
        alias: 'PassRate',
        numeratorField: 'PassedAssessments',
        denominatorField: 'EligibleAssessments',
        multiplier: 100,
        decimals: 0,
        legendDisplay: 'First-Time Pass Rate',
        color: '#07967f',
        valueSuffix: '%'
      }],
      staticFilter: initialPerformanceFilter,
      sortField: 'PassRate',
      maxItems: 10,
      showValues: true,
      valueAxisMax: 100,
      drillDownKey: 'checkrides'
    }
  ],
  drillDowns: [{
    key: 'checkrides',
    title: 'Checkrides',
    item: 'CheckRideRequest',
    targetKeyField: 'CheckRideRequest.CheckRideRequestId',
    gridConfigKey: 'school-admin-financials',
    gridConfigKeyPart2: 'dep-admin-financials'
  }],
  defaultDrillDownKey: 'checkrides',
  defaultDrillDown: {
    drillDownKey: 'checkrides',
    sourceKeyField: attrs.CheckRideRequestId.fieldNameWithItemPrefix
  }
};

function chartRequest(
  chartType: DwChartTypes,
  categoryField: string,
  categoryDisplayField: string,
  hideLegend = false
): ChartDataRequest {
  const request = new ChartDataRequest();
  request.chartType = chartType;
  request.title = '';
  request.categoryField = categoryField;
  request.categoryDisplayField = categoryDisplayField;
  request.hideLegend = hideLegend;
  request.queryRequest = { item: 'roCheckrideFactBase' };
  return request;
}

function equalFilter(field: string, value: any): DwQueryFilterGroup {
  const group = new DwQueryFilterGroup();
  const queryHelper = new DwQueryHelper();
  group.AddFilterAnd(queryHelper.filters.Equal(field, value));
  return group;
}

function greaterThanFilter(field: string, value: number): DwQueryFilterGroup {
  const group = new DwQueryFilterGroup();
  const queryHelper = new DwQueryHelper();
  group.AddFilterAnd(queryHelper.filters.GreaterThan(field, value));
  return group;
}

function orFilter(filters: Array<{ field: string; value: any }>): DwQueryFilterGroup {
  const group = new DwQueryFilterGroup();
  const queryHelper = new DwQueryHelper();
  filters.forEach(filter => group.AddFilterOr(queryHelper.filters.Equal(filter.field, filter.value)));
  return group;
}

function mergeFilters(...groups: DwQueryFilterGroup[]): DwQueryFilterGroup {
  const merged = new DwQueryFilterGroup();
  groups.filter(group => group?.Filters?.length).forEach(group => merged.AddFilterAnd(group));
  return merged;
}

function formatNumber(value: any): string {
  const number = Number(value) || 0;
  return number.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
